import { cn } from "@/utils";

/**
 * Hover-revealed "Open" pill that sits just left of a row's kebab. Rows open
 * on double click (single click selects / expands), so this is the
 * discoverable single-click surface for opening a resource directly.
 *
 * Hidden until the row (`group/row`) is hovered. In-flow it takes its width
 * from the row's flexible title, which truncates further — the kebab and any
 * trailing controls stay put. Rows whose layout can't host an in-flow child
 * here (browser list rows, `RailRow`-based rows) position it absolutely via
 * `className`, where the solid background covers the text it overlaps.
 */
export function RailRowOpenButton({
  label,
  onOpen,
  className,
}: {
  /** Resource name — labels the button for assistive tech. */
  label: string;
  onOpen: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={`Open ${label}`}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      // A fast second click must not bubble a double-click open to the row.
      onDoubleClick={(e) => e.stopPropagation()}
      // Same as the kebab: don't arm the row's dnd-kit drag from this control.
      onPointerDown={(e) => e.stopPropagation()}
      // Don't take focus on mousedown — same composer focus-ring reasoning as
      // the row title buttons.
      onMouseDown={(e) => e.preventDefault()}
      className={cn(
        "hidden h-5 shrink-0 select-none items-center rounded bg-n-10 px-1.5 text-xs font-medium text-n-70 hover:bg-n-20 hover:text-n-90 group-hover/row:inline-flex",
        className,
      )}
    >
      Open
    </button>
  );
}
